/**
 * Single source of truth for how a user option links to price-DB article
 * numbers (`IPriceDataInfo.name`). Used by the pricing engine, the options
 * editor (embedded price forms) and the backend rename-sync — they must all
 * derive the same codes.
 */

const DIRECTION_SUFFIX = /\s(links|rechts|vorne|hinten)$/g;

/** Hinge option names carry a direction suffix that pricing strips. */
export function stripDirectionSuffix(value: string): string {
  return value.replaceAll(DIRECTION_SUFFIX, "");
}

/** Key for codesByThickness lookup (e.g. 6.5 → "6_5", 9 → "9"). */
export function thicknessKey(strength: number): string {
  return String(strength).replace(".", "_");
}

/**
 * The article code embedded in an option name. Article numbers never contain
 * spaces, but options for the same article may append an installation location
 * ("ROL37451S hinten", "ROL37451S vorne rechts"). Keep only the leading token
 * so all such variants match the same price entry.
 */
export function articleCodeFromName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

export interface ArticleCodeOptionLike {
  kind?: string;
  name?: string;
  /** PanelColor only: material code per panel thickness (Holzstärke). */
  codesByThickness?: Record<string, string>;
  /**
   * AngleSectionType only: a "composite" Kantenschutz references two source
   * options (Korpus/Deckel) by name instead of owning its own article number.
   */
  variants?: {
    corpusCode?: string;
    coverCode?: string;
  };
  /**
   * CornerType / CornerBraceType only: the "L-Variante" owns its own article
   * number(s) alongside the name-derived one. `longCode` (CornerBraceType) is
   * the article for the default long-edge mesh, `code` the one for the compact
   * L-variant mesh — the build steps swap `partSelection.name` to whichever
   * applies, so both must be maintainable and rename-protected.
   */
  angleVariant?: {
    code?: string;
    longCode?: string;
  };
}

/** Kinds whose options may carry an `angleVariant` sub-object. */
const ANGLE_VARIANT_KINDS = new Set(["CornerType", "CornerBraceType"]);

/** The extra article numbers an active L-Variante contributes, in stable order. */
function angleVariantCodes(option: ArticleCodeOptionLike): string[] {
  if (!ANGLE_VARIANT_KINDS.has(option.kind ?? "")) {
    return [];
  }
  const variant = option.angleVariant;
  if (!variant) {
    return [];
  }
  return [variant.longCode, variant.code]
    .map((code) => code?.trim() ?? "")
    .filter((code) => code.length > 0);
}

/** A variant reference is set when it is a non-empty, non-sentinel code. */
function isVariantCodeSet(code: string | undefined): boolean {
  return !!code && code !== "__none__" && code.trim() !== "";
}

/**
 * A composite option references other options and owns no price data of its
 * own — the price lives at the referenced source options.
 *
 * Detected by a slash in the name: composites are named after the sources they
 * combine (e.g. "STW1377/STW1378", "ECK4044/ECK4043A"), a convention that holds
 * across every kind. An AngleSectionType carrying an explicit Korpus/Deckel
 * variant reference is also treated as composite even if its name lacks a slash.
 */
export function isCompositeOption(option: ArticleCodeOptionLike): boolean {
  if (option.name?.includes("/")) {
    return true;
  }
  return (
    option.kind === "AngleSectionType" &&
    (isVariantCodeSet(option.variants?.corpusCode) ||
      isVariantCodeSet(option.variants?.coverCode))
  );
}

/**
 * The source article codes a composite references (never its own). Slash names
 * enumerate the sources they combine ("STW1377/STW1378"), and an
 * AngleSectionType may additionally carry explicit Korpus/Deckel references.
 */
function compositeReferencedCodes(option: ArticleCodeOptionLike): string[] {
  const codes: string[] = [];
  // Only a slash name enumerates sources; a plain name (variant composite) is
  // just a label, not a code.
  if (option.name?.includes("/")) {
    for (const part of option.name.split("/")) {
      const trimmed = part.trim();
      if (trimmed) codes.push(trimmed);
    }
  }
  if (isVariantCodeSet(option.variants?.corpusCode)) {
    codes.push(option.variants?.corpusCode?.trim() ?? "");
  }
  if (isVariantCodeSet(option.variants?.coverCode)) {
    codes.push(option.variants?.coverCode?.trim() ?? "");
  }
  // A composite corner brace prices through its own angleVariant codes, which
  // need not appear in the slash name ("ECK4044/ECK4043A" resolves to
  // "ECK4043") — without these the referenced entry is unprotected.
  codes.push(...angleVariantCodes(option));
  return [...new Set(codes.filter((code) => code.length > 0))];
}

/**
 * Every article code an option is associated with: the codes it links to for
 * pricing (source options) OR, for a composite, the source codes it combines.
 * Used to detect whether a price entry is "in use" before its code is renamed —
 * a rename would orphan the referencing option's price lookup.
 */
export function articleCodesReferencedByOption(
  option: ArticleCodeOptionLike,
): string[] {
  if (isCompositeOption(option)) {
    return compositeReferencedCodes(option);
  }
  return articleCodesForOption(option);
}

/**
 * The article numbers a user option links to in the price DB, in a stable
 * order. Kind values are the `LTCaseUserOption` enum values (kept as string
 * literals — the pricing package must not depend on the manufacturer package).
 *
 * - Composite options (name contains a slash, or an AngleSectionType with a
 *   Korpus/Deckel reference): no articles of their own — the price lives at the
 *   referenced source options.
 * - PanelColor ("Holzfarben"): one code per thickness, deduplicated (codes
 *   like "DEFAULT" may be shared across thicknesses and options).
 * - PanelStrength ("Holzstärken"): no articles of their own.
 * - HingeType / ProfileType: pricing derives the code from the option name
 *   (direction suffix stripped resp. "i" removed, see getPriceForPart).
 * - CornerType / CornerBraceType with an active "L-Variante": the name-derived
 *   code plus the angleVariant's own article number(s), deduplicated. The build
 *   steps swap in `angleVariant.code` (resp. `longCode`) as the part name, so
 *   those entries are priced just as directly as the base one.
 * - Everything else links by the leading token of the name — a trailing
 *   installation location ("ROL37451S hinten") is dropped, since article
 *   numbers never contain spaces.
 *
 * The name-derived code is always first: `renameLinkedPriceData` renames only
 * `[0]`, and the embedded price forms zip lookup against display codes by index.
 */
export function articleCodesForOption(option: ArticleCodeOptionLike): string[] {
  const name = option.name?.trim() ?? "";

  if (isCompositeOption(option)) {
    return [];
  }

  switch (option.kind) {
    case "PanelColor": {
      const codes = Object.entries(option.codesByThickness ?? {})
        .sort(
          ([a], [b]) =>
            Number.parseFloat(a.replace("_", ".")) -
            Number.parseFloat(b.replace("_", ".")),
        )
        .map(([, code]) => code.trim())
        .filter((code) => code.length > 0);
      return [...new Set(codes)];
    }
    case "PanelStrength":
      return [];
    case "HingeType":
      return name ? [stripDirectionSuffix(name)] : [];
    case "ProfileType":
      return name ? [name.replace("i", "")] : [];
    default: {
      const codes = name ? [articleCodeFromName(name)] : [];
      codes.push(...angleVariantCodes(option));
      return [...new Set(codes.filter((code) => code.length > 0))];
    }
  }
}
